import React, { useState, useEffect } from 'react';
import { mcrService } from '../../../../services/mcrService';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme,
  InputAdornment,
  Divider,
  Stack
} from '@mui/material';
import {
  AccessTime,
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
  RequestQuote,
  Visibility,
  AttachMoney,
  Person,
  ListAlt,
  Receipt,
  Description
} from '@mui/icons-material';

const MunicipalCivilRegistryHome = ({ user }) => {
  const theme = useTheme();
  const [pendingOrders, setPendingOrders] = useState([]);
  const [paidOrders, setPaidOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewingOrder, setViewingOrder] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [orNumber, setOrNumber] = useState('');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    const result = await mcrService.getAllOrders();
    if (result.success) {
      // Filter pending and paid orders
      const pending = result.data.filter(order => order.status === 'pending');
      const paid = result.data.filter(order => order.status === 'paid')
        .slice(0, 10); // Show only 10 most recent paid orders
      setPendingOrders(pending);
      setPaidOrders(paid);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const calculateAge = (birthdate) => {
    if (!birthdate) return '';
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleViewOrder = async (order) => {
    setLoading(true);
    const result = await mcrService.getOrderWithItems(order.id);
    if (result.success) {
      setViewingOrder(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const getOrderBreakdown = (order) => {
    if (!order) return { items: [], totalBasic: 0, totalDST: 0, totalDSTCount: 0, totalAmount: 0 };
    
    const items = order.items || [];
    let totalDST = 0;
    let totalDSTCount = 0;

    const processedItems = items.map((item) => {
      const qty = parseInt(item.quantity || 1, 10);
      const unitPrice = parseFloat(item.unit_price || 0);
      const dstAmount = parseFloat(item.dst_amount || 0);
      
      const basicAmount = (item.basic_amount !== undefined && item.basic_amount !== null && !isNaN(item.basic_amount) && parseFloat(item.basic_amount) > 0)
        ? parseFloat(item.basic_amount)
        : Math.max(0, unitPrice - dstAmount);
      
      const basicSubtotal = basicAmount * qty;
      const itemDSTTotal = dstAmount * qty;
      
      if (dstAmount > 0) {
        totalDST += itemDSTTotal;
        totalDSTCount += qty;
      }

      return {
        ...item,
        qty,
        basicAmount,
        basicSubtotal,
        dstAmount,
        itemDSTTotal
      };
    });

    const totalBasic = processedItems.reduce((sum, i) => sum + i.basicSubtotal, 0);
    const totalAmount = parseFloat(order.total_amount) || (totalBasic + totalDST);

    return {
      items: processedItems,
      totalBasic,
      totalDST,
      totalDSTCount,
      totalAmount
    };
  };

  const openPaymentModal = async (order) => {
    setOrNumber('');
    if (!order.items || order.items.length === 0) {
      setLoading(true);
      const result = await mcrService.getOrderWithItems(order.id);
      setLoading(false);
      if (result.success && result.data) {
        setProcessingPayment(result.data);
      } else {
        setProcessingPayment(order);
      }
    } else {
      setProcessingPayment(order);
    }
  };

  const handleProcessPayment = async () => {
    if (!orNumber.trim()) {
      setError('Please enter OR Number');
      return;
    }

    setLoading(true);
    // Get the current user's full name
    const processedBy = `${user.firstname} ${user.lastname}`;
    const result = await mcrService.updateOrderStatus(processingPayment.id, 'paid', orNumber, processedBy);
    
    if (result.success) {
      setSuccess(`Payment processed successfully! OR #${orNumber}`);
      setProcessingPayment(null);
      setOrNumber('');
      loadOrders();
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <Box sx={{ flexGrow: 1, pb: 4 }}>
      <Container maxWidth="xl">
        {/* Welcome Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
            Welcome to Municipal Civil Registry, {user?.firstname}! 👋
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Municipal Civil Registry Management System
          </Typography>
        </Box>

        {/* Pending Orders Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTime sx={{ mr: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                Pending Orders of Payment
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {pendingOrders.length} order{pendingOrders.length !== 1 ? 's' : ''} awaiting payment
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Refresh />}
            onClick={loadOrders}
            disabled={loading}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
          >
            Refresh
          </Button>
        </Paper>

        {/* Pending Orders List */}
        {pendingOrders.length === 0 && !loading ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: '#f0fdf4',
              border: '2px dashed #bbf7d0',
              borderRadius: 2,
              mb: 4
            }}
          >
            <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" color="text.primary" gutterBottom>
              All Caught Up!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              No pending orders of payment at the moment
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell>Order #</TableCell>
                  <TableCell>Client Name</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {order.order_number}
                    </TableCell>
                    <TableCell>{order.client?.name || 'N/A'}</TableCell>
                    <TableCell>
                      {order.client?.birthdate
                        ? `${calculateAge(order.client?.birthdate)} years`
                        : (order.client?.age ? `${order.client.age} years` : 'N/A')}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {order.client?.address || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`₱${parseFloat(order.total_amount).toFixed(2)}`}
                        color="warning"
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={() => handleViewOrder(order)}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<AttachMoney />}
                          onClick={() => openPaymentModal(order)}
                        >
                          Pay
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Recent Paid Orders Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
            color: 'white',
            borderRadius: 2
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CheckCircle sx={{ mr: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                Recent Paid Orders
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Last {paidOrders.length} completed payment{paidOrders.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Paper>

        {/* Paid Orders List */}
        {paidOrders.length === 0 && !loading ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: '#eff6ff',
              border: '2px dashed #bfdbfe',
              borderRadius: 2
            }}
          >
            <RequestQuote sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" color="text.primary" gutterBottom>
              No Paid Orders Yet
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Completed payments will appear here
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell>Order #</TableCell>
                  <TableCell>OR Number</TableCell>
                  <TableCell>Client Name</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Payment Date</TableCell>
                  <TableCell>Processed By</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paidOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {order.order_number}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.or_number || 'N/A'}
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{order.client?.name || 'N/A'}</TableCell>
                    <TableCell>
                      {order.client?.birthdate
                        ? `${calculateAge(order.client?.birthdate)} years`
                        : (order.client?.age ? `${order.client.age} years` : 'N/A')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`₱${parseFloat(order.total_amount).toFixed(2)}`}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell>
                      {order.paid_at
                        ? new Date(order.paid_at).toLocaleDateString() + ' ' +
                          new Date(order.paid_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {order.processed_by || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => handleViewOrder(order)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* View Order Dialog */}
        <Dialog
          open={Boolean(viewingOrder)}
          onClose={() => setViewingOrder(null)}
          maxWidth="md"
          fullWidth
        >
          {viewingOrder && (
            <>
              <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center' }}>
                <Description sx={{ mr: 1 }} />
                Order Details - {viewingOrder.order_number}
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={3}>
                  {/* Client Info */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <Person sx={{ mr: 1 }} />
                        Client Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Name</Typography>
                          <Typography variant="body1" fontWeight="medium">{viewingOrder.client?.name}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Age</Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {viewingOrder.client?.birthdate
                              ? `${calculateAge(viewingOrder.client?.birthdate)} years`
                              : (viewingOrder.client?.age ? `${viewingOrder.client.age} years` : 'N/A')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Address</Typography>
                          <Typography variant="body1" fontWeight="medium">{viewingOrder.client?.address || viewingOrder.client?.barangay || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Recommended By</Typography>
                          <Typography variant="body1" fontWeight="medium">{viewingOrder.client?.recommended_by || 'N/A'}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Order Items & Breakdown */}
                  <Grid item xs={12}>
                    <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <ListAlt sx={{ mr: 1 }} />
                      Detailed Breakdown of Charges
                    </Typography>
                    {(() => {
                      const vBreakdown = getOrderBreakdown(viewingOrder);
                      return (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: 'grey.100' }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Charge / Fee Description</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600, width: 60 }}>Qty</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, width: 100 }}>Rate</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, width: 110 }}>Subtotal</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {vBreakdown.items.map((item, idx) => (
                                <TableRow key={item.id || idx}>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="medium">{item.service_name}</Typography>
                                    {item.subcategory && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {item.category && `${item.category} → `}{item.subcategory}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="center">{item.qty}</TableCell>
                                  <TableCell align="right">₱{item.basicAmount.toFixed(2)}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                                    ₱{item.basicSubtotal.toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))}

                              {/* Combined DST Row */}
                              {vBreakdown.totalDST > 0 && (
                                <TableRow sx={{ bgcolor: 'warning.50', borderTop: '1px dashed #fde68a' }}>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" fontWeight="bold" sx={{ color: 'warning.dark' }}>
                                        Documentary Stamp Tax (DST)
                                      </Typography>
                                      <Chip
                                        label="Combined"
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
                                      />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Combined DST ({vBreakdown.totalDSTCount} {vBreakdown.totalDSTCount === 1 ? 'doc stamp' : 'doc stamps'})
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                                    {vBreakdown.totalDSTCount}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                    ₱{(vBreakdown.totalDST / vBreakdown.totalDSTCount).toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                                    ₱{vBreakdown.totalDST.toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              )}

                              <TableRow sx={{ bgcolor: 'primary.50', borderTop: '2px solid', borderColor: 'primary.main' }}>
                                <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>TOTAL:</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.1rem' }}>
                                  ₱{vBreakdown.totalAmount.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      );
                    })()}
                  </Grid>

                  {/* Payment Info if paid */}
                  {viewingOrder.status === 'paid' && (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50', borderColor: 'success.main' }}>
                        <Typography variant="h6" color="success.dark" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircle sx={{ mr: 1 }} />
                          Payment Details
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">OR Number</Typography>
                            <Typography variant="body1" fontWeight="bold" color="success.dark">
                              {viewingOrder.or_number || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Date Paid</Typography>
                            <Typography variant="body1" fontWeight="bold" color="success.dark">
                              {viewingOrder.paid_at ? new Date(viewingOrder.paid_at).toLocaleDateString() : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">Processed By</Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {viewingOrder.processed_by || 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  )}

                  <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    <Chip
                      label={viewingOrder.status === 'paid' ? 'PAID' : 'PENDING'}
                      color={viewingOrder.status === 'paid' ? 'success' : 'warning'}
                      sx={{ fontWeight: 'bold', px: 2 }}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setViewingOrder(null)}>Close</Button>
                {viewingOrder.status === 'pending' && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<AttachMoney />}
                    onClick={() => {
                      openPaymentModal(viewingOrder);
                      setViewingOrder(null);
                    }}
                  >
                    Process Payment
                  </Button>
                )}
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Process Payment Dialog */}
        <Dialog
          open={Boolean(processingPayment)}
          onClose={() => setProcessingPayment(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2.5, overflow: 'hidden' }
          }}
        >
          {processingPayment && (() => {
            const breakdown = getOrderBreakdown(processingPayment);
            return (
              <>
                <DialogTitle
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.75,
                    px: 3
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AttachMoney sx={{ mr: 1, fontSize: 26 }} />
                    <Typography variant="h6" fontWeight="bold" component="span">
                      Process Payment
                    </Typography>
                  </Box>
                  <Chip
                    label="MCR Billing"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  />
                </DialogTitle>

                <DialogContent sx={{ p: 3, pt: '20px !important' }}>
                  {/* Order & Client Information */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2.5,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      borderColor: 'grey.200'
                    }}
                  >
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Order Number
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                          {processingPayment.order_number}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Client Name
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {processingPayment.client?.name || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Detailed Breakdown of Charges */}
                  <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Receipt sx={{ fontSize: 18, color: 'primary.main' }} />
                    Breakdown of Charges
                  </Typography>

                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      borderColor: 'grey.200',
                      overflow: 'hidden'
                    }}
                  >
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary', py: 1 }}>
                            Fee / Charge Description
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary', py: 1, width: 55 }}>
                            Qty
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary', py: 1, width: 85 }}>
                            Rate
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary', py: 1, width: 95 }}>
                            Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Basic Service Fees */}
                        {breakdown.items.map((item, index) => (
                          <TableRow key={item.id || index} hover>
                            <TableCell sx={{ py: 1.2 }}>
                              <Typography variant="body2" fontWeight="600" color="text.primary">
                                {item.service_name}
                              </Typography>
                              {item.subcategory && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {item.category ? `${item.category} → ` : ''}{item.subcategory}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1.2, fontSize: '0.875rem' }}>
                              {item.qty}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.2, fontSize: '0.875rem', color: 'text.secondary' }}>
                              ₱{item.basicAmount.toFixed(2)}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.2, fontWeight: 600, fontSize: '0.875rem' }}>
                              ₱{item.basicSubtotal.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* Combined DST Row (if DST is present) */}
                        {breakdown.totalDST > 0 && (
                          <TableRow sx={{ bgcolor: '#fffbeb', borderTop: '1px dashed #fde68a' }}>
                            <TableCell sx={{ py: 1.2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight="bold" sx={{ color: '#92400e' }}>
                                  Documentary Stamp Tax (DST)
                                </Typography>
                                <Chip
                                  label="Combined"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    bgcolor: '#fef3c7',
                                    color: '#b45309',
                                    border: '1px solid #fde68a'
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" sx={{ color: '#b45309' }} display="block">
                                Combined DST ({breakdown.totalDSTCount} {breakdown.totalDSTCount === 1 ? 'doc stamp' : 'doc stamps'})
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1.2, fontWeight: 'bold', color: '#92400e', fontSize: '0.875rem' }}>
                              {breakdown.totalDSTCount}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.2, fontSize: '0.875rem', color: '#92400e' }}>
                              ₱{(breakdown.totalDST / breakdown.totalDSTCount).toFixed(2)}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.2, fontWeight: 'bold', color: '#92400e', fontSize: '0.875rem' }}>
                              ₱{breakdown.totalDST.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Fallback if no items */}
                        {breakdown.items.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                              Standard Service Fee
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Total Row */}
                        <TableRow sx={{ bgcolor: '#ecfdf5', borderTop: '2px solid #059669' }}>
                          <TableCell colSpan={3} sx={{ py: 1.5, fontWeight: 800, fontSize: '0.9rem', color: '#065f46' }}>
                            TOTAL AMOUNT DUE
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.5, fontWeight: 800, color: '#059669', fontSize: '1.2rem' }}>
                            ₱{breakdown.totalAmount.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* OR Number Input Field */}
                  <TextField
                    fullWidth
                    autoFocus
                    label="Official Receipt (OR) Number"
                    variant="outlined"
                    placeholder="e.g. 1234567"
                    value={orNumber}
                    onChange={(e) => setOrNumber(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Receipt sx={{ color: 'primary.main' }} />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Please enter the official receipt number for this payment"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 1, borderTop: '1px solid', borderColor: 'grey.200', gap: 1 }}>
                  <Button
                    onClick={() => setProcessingPayment(null)}
                    variant="outlined"
                    color="inherit"
                    sx={{ borderRadius: 1.5, px: 2.5 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleProcessPayment}
                    disabled={loading || !orNumber.trim()}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                    sx={{ borderRadius: 1.5, px: 3, fontWeight: 'bold' }}
                  >
                    Confirm Payment
                  </Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>

        {/* Feedback Snackbar */}
        <Snackbar
          open={Boolean(error || success)}
          autoHideDuration={3000}
          onClose={() => { setError(''); setSuccess(''); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => { setError(''); setSuccess(''); }}
            severity={error ? 'error' : 'success'}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {error || success}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default MunicipalCivilRegistryHome;
